import * as React from 'react';
import {styled} from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

export const verificationCodeError =
  'We do not recognize that code. Please try again.';

type Props = {
  codeLength?: number;
  value: string;
  onChange: (a: string) => unknown;
  onBlur?: Function;
  onFocus?: Function;
  name?: string;
  disabled?: boolean;
  helperText?: string | null;
  error?: string | null;
};

const NumberInput = styled('input')<{
  inputStatus: 'error' | 'success' | 'default';
}>(({inputStatus, theme}) => {
  const statusColor =
    inputStatus === 'error'
      ? theme.palette.error.main
      : theme.palette.text.primary;
  return {
    border: `1px solid ${statusColor}`,
    color: `${statusColor}`,
    backgroundColor: 'transparent',
    width: '40px',
    height: '50px',
    boxShadow: 'none',
    margin: 0,
    padding: 0,
    fontSize: '34px',
    textAlign: 'center',
    borderRadius: '2px',
  };
});

export const VerificationNumberInput: React.FC<Props> = ({
  codeLength = 5,
  value,
  onChange,
  onBlur,
  onFocus,
  disabled,
  error,
  helperText,
  ...otherProps
}) => {
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  const setFocus = (i: number): void => {
    if (inputRefs.current[i]) {
      inputRefs.current[i]?.focus();
    }
  };

  React.useEffect(() => {
    if (value.length !== 0) {
      setFocus(value.length);
    }
  }, [value]);

  const getValueArray = (val: string): string[] => {
    const propValueArray = val ? val.split('') : [];
    const valueArray = new Array(codeLength).fill('');
    // eslint-disable-next-line no-return-assign
    valueArray.forEach((_, i) => (valueArray[i] = propValueArray[i] || ''));
    return valueArray;
  };

  const getFocusedIndex = (): number =>
    inputRefs.current.findIndex(ref => ref === document.activeElement);

  const handleFocus = (i: number): unknown => {
    const valueArray = getValueArray(value);

    // Focus on First Empty Input
    const bestFocusedIndex = valueArray.findIndex(val => !val);
    if (bestFocusedIndex < i && bestFocusedIndex !== -1) {
      return setFocus(bestFocusedIndex);
    }

    const hasSubsequentValues = valueArray.slice(i).some(Boolean);

    if (hasSubsequentValues) {
      onChange(valueArray.fill('', i).join(''));
    }
  };

  const handleBlur = (e: any): void => {
    onBlur && onBlur(e);
  };

  const handleKeyDown = (e: any): void => {
    if (e.key === 'Backspace') {
      const focusedIndex = getFocusedIndex();
      const nextIndex = focusedIndex > 0 ? focusedIndex - 1 : 0;
      setFocus(nextIndex);
    }
  };

  const handleChange = async (e: any, i: number): Promise<unknown> => {
    const {value: inputValue} = e.target;
    if (typeof Number(inputValue) !== 'number') return;
    const valueArray = getValueArray(value);

    if (inputValue.length > 1) {
      const pastedValueArray = inputValue.replace(/ /g, '').split('');
      valueArray.forEach((char, index) => {
        if (index < i) return;

        valueArray[index] = pastedValueArray[index - i];
      });
      onChange(valueArray.join(''));
      return;
    }

    valueArray[i] = inputValue;
    onChange(valueArray.join(''));
  };

  const valueArray: string[] = getValueArray(value);

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          flexDirection: 'row',
          justifyContent: 'center',
          gap: '10px',
        }}
      >
        {Array.from({length: codeLength}).map((_, i) => (
          <NumberInput
            {...otherProps}
            key={i}
            value={valueArray[i]}
            aria-label={`${otherProps.name}-${i}`}
            // eslint-disable-next-line no-return-assign
            ref={node => (inputRefs.current[i] = node)}
            onChange={e => handleChange(e, i)}
            onBlur={handleBlur}
            onFocus={() => handleFocus(i)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            maxLength={codeLength * 2 - i}
            inputStatus={error ? 'error' : 'default'}
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            type="text"
          />
        ))}
      </Box>
      <Box sx={{textAlign: 'center'}}>
        <Typography variant="body2" color={error ? 'error' : undefined}>
          {error ?? helperText}
        </Typography>
      </Box>
    </Box>
  );
};
